<template>
  <div
    class="fancy-icon"
    :class="[color, size, align && `align-${align}`]"
  >
    <BaseIcon
      :id="icon"
      :size="size"
    />
  </div>
</template>

<script setup lang="ts">
import BaseIcon from '../atoms/BaseIcon.vue';

/**
 * Circular bordered icon wrapper with semantic color theming
 *
 * Decorative icon container with circular border, background fill, and
 * semantic color variants. Commonly used in alerts, dialogs, and status
 * indicators to provide visual context through color coding.
 *
 * @example Basic usage with semantic color
 * ```vue
 * <FancyIcon icon="check-circle" color="success" />
 * ```
 *
 * @example Warning indicator in dialog
 * ```vue
 * <FancyIcon icon="warning" color="warning" size="lg" />
 * ```
 *
 * @example Error state in alert
 * ```vue
 * <FancyIcon icon="x-circle" color="error" />
 * ```
 *
 * @example Info indicator with custom icon
 * ```vue
 * <FancyIcon icon="lightbulb" color="info" size="sm" />
 * ```
 *
 * @example Primary accent in card header
 * ```vue
 * <FancyIcon icon="star" color="primary" />
 * ```
 */
export interface FancyIconProps {
  /**
   * Phosphor icon name to display
   * Uses BaseIcon component internally
   * @example "check-circle", "warning", "info", "x-circle"
   */
  icon: string;

  /**
   * Semantic color variant determining background, border, and icon colors
   * Maps to design system color tokens for consistent theming
   * - primary: Blue accent for primary actions/info
   * - secondary: Orange accent for secondary emphasis
   * - error: Red for errors and destructive actions
   * - warning: Yellow/amber for warnings and cautions
   * - success: Green for success states and confirmations
   * - info: Purple for informational messages
   * - neutral: Gray for neutral/default states
   * @default 'primary'
   */
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'info' | 'neutral';

  /**
   * Size variant controlling the inner icon scale
   * Padding is consistent across all sizes; only the icon scales
   * - sm: Compact size for inline usage and alerts
   * - md: Standard size for most use cases
   * - lg: Prominent size for modal dialogs and headers
   * - xl: Extra large for page headers and hero sections
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg' | 'xl';

  /**
   * Text alignment for the icon container
   * @default undefined
   */
  align?: 'center' | 'end' | 'start';
}

withDefaults(defineProps<FancyIconProps>(), {
  color: 'primary',
  size: 'md',
});
</script>

<style scoped>
.fancy-icon {
  border-radius: var(--border-radius-full);
  flex-shrink: 0;
  background: var(--fancy-icon-background);
  color: var(--fancy-icon-color);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  padding: var(--gap-xs);

  &.xl {
    padding: var(--gap-md);
  }

  &[class*='align-'] {
    display: flex;
  }

  &.align-center {
    margin-inline: auto;
  }

  &.align-end {
    margin-left: auto;
  }

  &.primary {
    --fancy-icon-background: var(--color-primary-100);
    --fancy-icon-color: var(--color-primary-300);
  }

  &.secondary {
    --fancy-icon-background: var(--color-secondary-100);
    --fancy-icon-color: var(--color-secondary-300);
  }

  &.error {
    --fancy-icon-background: var(--color-error-100);
    --fancy-icon-color: var(--color-error-300);
  }

  &.warning {
    --fancy-icon-background: var(--color-warning-100);
    --fancy-icon-color: var(--color-warning-300);
  }

  &.success {
    --fancy-icon-background: var(--color-success-100);
    --fancy-icon-color: var(--color-success-300);
  }

  &.info {
    --fancy-icon-background: var(--color-info-100);
    --fancy-icon-color: var(--color-info-300);
  }

  &.neutral {
    --fancy-icon-background: var(--color-neutral-200);
    --fancy-icon-color: var(--color-neutral-600);
  }
}
</style>
